/**
 * Checkout Terms Composable
 * Phase 5: Booking Submission — terms & conditions gating
 *
 * Owns the terms section of checkout step 2:
 * - vee-validate form instance with Zod schema (factory, conditional on visibility)
 * - CheckboxList options array (statutory + contractual + privacy + newsletter)
 * - Composed HTML label strings for the two dynamic checkboxes
 *
 * Visibility / requirement rules:
 * - Statutory checkbox is rendered + required iff at least one of FORM/VVI links is present.
 * - Contractual checkbox is always rendered + required.
 * - Anchor tags collapse to plain text when the corresponding URL is missing
 *   (uses dedicated `*-nolink` translation keys to keep grammar correct).
 */
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useCheckoutStore } from '@/stores/checkout';
import { $t } from '@/composables/translations/useTranslate';
import { sanitizeInlineHtml } from '@/utils/sanitizeHtml';
import { createTermsFormSchema, type TermsFormValues } from '@/types/schemas/checkout/terms';
import { type CheckboxOption } from '@/components/ui/CheckboxList.vue';

export function useCheckoutTerms() {
  const checkout = useCheckoutStore();

  const validationSchema = computed(() =>
    toTypedSchema(createTermsFormSchema(checkout.isStatutoryCheckboxVisible))
  );

  const { values, errors, validate, setFieldValue, setFieldError } = useForm<TermsFormValues>({
    validationSchema,
    initialValues: {
      termsStatutory: false,
      termsContractual: false,
      privacyPolicy: false,
      newsletterOptIn: false,
    },
  });

  const hasAttemptedSubmit = ref(false);

  watch(
    () => checkout.isStatutoryCheckboxVisible,
    (visible) => {
      if (!visible) {
        setFieldValue('termsStatutory', false);
        setFieldError('termsStatutory', undefined);
      }
    }
  );

  const hasInsuranceSelected = computed(() => checkout.selectedInsurance !== null);

  function handleChange(payload: { name: string; value: boolean }): void {
    setFieldValue(payload.name as keyof TermsFormValues, payload.value);
  }

  function markSubmitAttempted(): void {
    hasAttemptedSubmit.value = true;
  }

  const statutoryLabelHtml = computed(() => {
    const parts: string[] = [];
    parts.push($t('ibe.terms.statutory.intro', 'I have taken note of '));

    if (checkout.isPackage) {
      parts.push(
        $t(
          'ibe.terms.statutory.form',
          // The actual URL is in the Pimcore translation; this is just a dev placeholder.
          '<a href="#" target="_blank">the package travel directive form</a>'
        )
      );
    }
    if (checkout.isPackage && checkout.vviUrl) {
      parts.push($t('ibe.terms.statutory.connector', ' as well as '));
    }
    if (checkout.vviUrl) {
      parts.push(
        $t(
          'ibe.terms.statutory.vvi',
          '<a href="{url}" target="_blank">the pre-contractual information and entry requirements</a>',
          { url: checkout.vviUrl }
        )
      );
    }

    parts.push($t('ibe.terms.statutory.outro', '.'));
    return sanitizeInlineHtml(parts.join(''));
  });

  const contractualLabelHtml = computed(() => {
    const parts: string[] = [];
    parts.push($t('ibe.terms.contractual.intro', 'I accept '));

    parts.push(
      checkout.providerTermsLink
        ? $t(
            'ibe.terms.contractual.gtc',
            '<a href="{url}" target="_blank">the General Terms and Conditions of the organizer</a>',
            { url: checkout.providerTermsLink }
          )
        : $t(
            'ibe.terms.contractual.gtc-nolink',
            'the General Terms and Conditions of the organizer'
          )
    );

    if (checkout.cancellationTermsLink) {
      parts.push(
        $t(
          'ibe.terms.contractual.cancellation',
          ' and <a href="{url}" target="_blank">the cancellation policy</a>',
          { url: checkout.cancellationTermsLink }
        )
      );
    }

    if (hasInsuranceSelected.value) {
      const insuranceUrl = $t('ibe.terms.insurance-url', '/versicherungsbedingungen');
      parts.push(
        $t(
          'ibe.terms.contractual.insurance',
          ' as well as <a href="{url}" target="_blank">the insurance conditions</a>',
          { url: insuranceUrl }
        )
      );
    }

    parts.push($t('ibe.terms.contractual.outro', '.'));
    return sanitizeInlineHtml(parts.join(''));
  });

  const checkboxOptions = computed<CheckboxOption[]>(() => {
    const options: CheckboxOption[] = [];

    options.push({
      id: 'checkbox-terms-contractual',
      name: 'termsContractual',
      required: true,
      slotName: 'termsContractual',
      hasError: hasAttemptedSubmit.value && !!errors.value.termsContractual,
      errorMessage: hasAttemptedSubmit.value ? errors.value.termsContractual : undefined,
    });

    if (checkout.isStatutoryCheckboxVisible) {
      options.push({
        id: 'checkbox-terms-statutory',
        name: 'termsStatutory',
        required: true,
        slotName: 'termsStatutory',
        hasError: hasAttemptedSubmit.value && !!errors.value.termsStatutory,
        errorMessage: hasAttemptedSubmit.value ? errors.value.termsStatutory : undefined,
      });
    }

    options.push({
      id: 'checkbox-privacy-policy',
      name: 'privacyPolicy',
      required: true,
      slotName: 'privacyPolicy',
      hasError: hasAttemptedSubmit.value && !!errors.value.privacyPolicy,
      errorMessage: hasAttemptedSubmit.value ? errors.value.privacyPolicy : undefined,
    });

    options.push({
      id: 'checkbox-newsletter',
      name: 'newsletterOptIn',
      label: $t(
        'ibe.checkout.terms.newsletter',
        'Yes, I would like to stay up to date and receive interesting offers from Vamos.'
      ),
    });

    return options;
  });

  return {
    values,
    errors,
    validate,
    handleChange,
    markSubmitAttempted,
    hasAttemptedSubmit,
    checkboxOptions,
    statutoryLabelHtml,
    contractualLabelHtml,
  };
}
